package com.fantasytenis.web.components.lineup

import androidx.compose.runtime.Composable
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.components.common.InfoIcon
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Player Card Component for Dashboard Layout
 *
 * Shows all critical player info at a glance:
 * - Ranking + Name + Country + Info icon
 * - Opponent + Matchup Badge + Match Time
 * - All 5 category costs
 * - SELECT button (when in selecting mode)
 */
@Composable
fun PlayerCard(
    player: TennisPlayerEntity,
    selectingCategory: Category?,
    isAlreadyInLineup: Boolean,
    remainingBudget: Int,
    onSelect: () -> Unit,
    onViewSkills: () -> Unit = {}
) {
    val isSelectingMode = selectingCategory != null
    val costForCategory = selectingCategory?.let { player.getCostForCategory(it) } ?: 0
    val canAfford = costForCategory <= remainingBudget
    val isDisabled = isAlreadyInLineup || (isSelectingMode && !canAfford)

    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(16.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.08)")
            if (isDisabled) {
                opacity(0.5)
            }
            if (isSelectingMode && !isDisabled) {
                property("box-shadow", "0 2px 12px rgba(0, 0, 0, 0.12)")
                property("cursor", "pointer")
            }
            property("transition", "all 0.2s ease")
        }
        if (isSelectingMode && !isDisabled) {
            onClick { onSelect() }
        }
    }) {
        // Row 1: Ranking + Name + Country
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(8.px)
                marginBottom(8.px)
            }
        }) {
            // Ranking badge
            Span({
                style {
                    backgroundColor(Colors.Primary)
                    color(Colors.White)
                    padding(2.px, 8.px)
                    borderRadius(4.px)
                    fontSize(13.px)
                    fontWeight(700)
                }
            }) {
                Text("#${player.atpRanking ?: "NR"}")
            }

            // Player name
            Span({
                style {
                    fontSize(16.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    flex(1)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                }
            }) {
                Text(player.displayName)
            }

            // Country
            Span({
                style {
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                    fontWeight(500)
                }
            }) {
                Text(player.country)
            }

            // Info icon (opens skill drawer)
            InfoIcon(
                onClick = onViewSkills,
                size = 32,  // Smaller than default 44px for card context
                stopPropagation = true
            )
        }

        // Row 2: Opponent + Matchup + Time
        if (player.opponentName != null) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    marginBottom(12.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                // Opponent
                Span({
                    style {
                        fontSize(13.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("vs ${formatOpponentName(player.opponentName!!)}")
                }

                // Matchup badge
                if (player.atpRanking != null && player.opponentRanking != null) {
                    MatchupIndicator(
                        playerRanking = player.atpRanking,
                        opponentRanking = player.opponentRanking,
                        showLabel = true,
                        compact = true
                    )
                }

                // Match time
                if (player.matchTime != null) {
                    Span({
                        style {
                            fontSize(12.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text(formatMatchTime(player.matchTime!!))
                    }
                }
            }
        } else {
            // No opponent yet
            Div({
                style {
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                    fontStyle("italic")
                    marginBottom(12.px)
                }
            }) {
                Text("Opponent TBA")
            }
        }

        // Row 3: All 5 category costs
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(4.px)
                marginBottom(if (isSelectingMode) 12.px else 0.px)
            }
        }) {
            Category.entries.forEach { category ->
                val cost = player.getCostForCategory(category)
                val isHighlighted = category == selectingCategory

                CategoryCostChip(
                    category = category,
                    cost = cost,
                    isHighlighted = isHighlighted
                )
            }
        }

        // Row 4: SELECT button (only in selecting mode)
        if (isSelectingMode && !isDisabled) {
            Div({
                style {
                    backgroundColor(getCategoryColor(selectingCategory!!))
                    color(Colors.White)
                    padding(10.px, 16.px)
                    borderRadius(8.px)
                    fontSize(14.px)
                    fontWeight(600)
                    textAlign("center")
                    cursor("pointer")
                    property("transition", "background-color 0.2s")
                }
            }) {
                Text("SELECT FOR \$${costForCategory}K")
            }
        }

        // Status badge for disabled states
        if (isAlreadyInLineup) {
            Div({
                style {
                    backgroundColor(Color("#fef3c7"))
                    color(Color("#92400e"))
                    padding(6.px, 12.px)
                    borderRadius(6.px)
                    fontSize(12.px)
                    fontWeight(600)
                    textAlign("center")
                    marginTop(8.px)
                }
            }) {
                Text("Already in Lineup")
            }
        } else if (isSelectingMode && !canAfford) {
            Div({
                style {
                    backgroundColor(Color("#fee2e2"))
                    color(Color("#991b1b"))
                    padding(6.px, 12.px)
                    borderRadius(6.px)
                    fontSize(12.px)
                    fontWeight(600)
                    textAlign("center")
                    marginTop(8.px)
                }
            }) {
                Text("Over Budget")
            }
        }
    }
}

/**
 * Individual category cost chip
 */
@Composable
private fun CategoryCostChip(
    category: Category,
    cost: Int,
    isHighlighted: Boolean
) {
    val categoryColor = getCategoryColor(category)
    val abbrev = getCategoryAbbrev(category)

    Span({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(2.px)
            padding(3.px, 6.px)
            borderRadius(4.px)
            fontSize(11.px)

            if (isHighlighted) {
                backgroundColor(categoryColor)
                color(Colors.White)
                fontWeight(700)
                property("box-shadow", "0 0 0 2px $categoryColor")
            } else {
                backgroundColor(Color("rgba(0, 0, 0, 0.04)"))
                color(Colors.TextSecondary)
                fontWeight(500)
            }
        }
    }) {
        Span({
            style {
                fontSize(9.px)
                if (!isHighlighted) {
                    color(categoryColor)
                }
            }
        }) {
            Text(abbrev)
        }
        Span({
            style {
                fontWeight(700)
            }
        }) {
            Text("$cost")
        }
    }
}

/**
 * Get the color for a category
 */
private fun getCategoryColor(category: Category): CSSColorValue {
    return when (category) {
        Category.SERVE -> Colors.Category.Serve
        Category.FOREHAND -> Colors.Category.Forehand
        Category.BACKHAND -> Colors.Category.Backhand
        Category.VOLLEYS -> Colors.Category.Volleys
        Category.FIGHTING -> Colors.Category.Fighting
    }
}

/**
 * Get abbreviated label for a category
 */
private fun getCategoryAbbrev(category: Category): String {
    return when (category) {
        Category.SERVE -> "SRV"
        Category.FOREHAND -> "FH"
        Category.BACKHAND -> "BH"
        Category.VOLLEYS -> "VOL"
        Category.FIGHTING -> "FGT"
    }
}

/**
 * Format opponent name to be more compact (first initial + last name)
 */
private fun formatOpponentName(name: String): String {
    val parts = name.split(" ")
    return if (parts.size >= 2) {
        "${parts.first().first()}. ${parts.drop(1).joinToString(" ")}"
    } else {
        name
    }
}

/**
 * Format match time from ISO string to readable format
 */
private fun formatMatchTime(isoTime: String): String {
    return try {
        // Use JavaScript Date for proper timezone handling
        val date = js("new Date(isoTime)") as kotlin.js.Date
        val hours = date.getHours()
        val minutes = date.getMinutes()
        val amPm = if (hours >= 12) "PM" else "AM"
        val hour12 = when {
            hours == 0 -> 12
            hours > 12 -> hours - 12
            else -> hours
        }
        val minStr = minutes.toString().padStart(2, '0')
        "$hour12:$minStr $amPm"
    } catch (e: Exception) {
        isoTime
    }
}
